import { useEffect, useState } from "react";
import { api, fmtDateTime, fileUrl } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { StatusPill } from "./Travel";
import ApprovalTimeline from "@/components/ApprovalTimeline";
import Pagination from "@/components/Pagination";
import { WarningCircle } from "@phosphor-icons/react";

const TAB_STATUS = {
  pending: ["pending", "info_requested"],
  completed: ["approved", "rejected"],
  paid: ["paid"],
};

export default function Approvals() {
  const { user } = useAuth();
  const isSuperAdmin = hasRole(user, "super_admin");
  const canDelete = isSuperAdmin;
  const [raw, setRaw] = useState([]);
  const [total, setTotal] = useState(0);
  const [active, setActive] = useState(null);
  const [comment, setComment] = useState("");
  const [tab, setTab] = useState("pending");
  const [filters, setFilters] = useState({ page: 1, page_size: 500 });

  const load = () => {
    const params = { ...filters };
    Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
    return api.get("/approvals", { params }).then((r) => {
      if (Array.isArray(r.data)) { setRaw(r.data); setTotal(r.data.length); }
      else { setRaw(r.data.items || []); setTotal(r.data.total || 0); }
    });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filters]);

  const openOne = async (a) => {
    try { const full = await api.get(`/approvals/${a.id}`); setActive(full.data); setComment(""); }
    catch { setActive(a); setComment(""); }
  };

  const act = async (action) => {
    if (!comment.trim()) return toast.error("Comment is required");
    try {
      const r = await api.post(`/approvals/${active.id}/action`, { action, comment });
      if (r?.data?.auto_paid) toast.success("Approved & auto-marked Paid");
      else toast.success("Action recorded");
      setActive(null); setComment(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const markPaid = async () => {
    if (!window.confirm(`Mark this ${active.type} as paid?`)) return;
    try { await api.post(`/approvals/${active.id}/mark-paid`); toast.success("Marked paid"); setActive(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const rejectApproved = async () => {
    // Super Admin: comment optional (backend accepts empty)
    if (!window.confirm("Reverse this approved/paid request back to Rejected? The applicant will be notified.")) return;
    try {
      await api.post(`/approvals/${active.id}/reject-approved`, { comment });
      toast.success("Reversed to rejected"); setActive(null); setComment(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete this ${a.type} approval and the underlying request? This cannot be undone.`)) return;
    try { await api.delete(`/approvals/${a.id}`); toast.success("Deleted"); setActive(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filtered = raw.filter((d) => TAB_STATUS[tab].includes(d.status));
  const counts = {
    pending: raw.filter((d) => TAB_STATUS.pending.includes(d.status)).length,
    completed: raw.filter((d) => TAB_STATUS.completed.includes(d.status)).length,
    paid: raw.filter((d) => TAB_STATUS.paid.includes(d.status)).length,
  };

  // Bulk mark-paid handled on Travel/Expense pages via BulkPaidBar (approval_id on each row)

  const renderDetails = (details) => {
    if (!details) return null;
    const shown = Object.entries(details).filter(([, v]) => v !== null && v !== undefined && v !== "");
    return (
      <div className="text-xs bg-slate-50 dark:bg-slate-800/40 border rounded p-3 space-y-1" style={{ borderColor: "hsl(var(--border))" }}>
        {shown.map(([k, v]) => {
          if (k === "lines" && Array.isArray(v)) {
            return (
              <div key={k}>
                <div className="opacity-60 mb-1">Items:</div>
                <ul className="pl-3 space-y-0.5">
                  {v.map((ln, i) => (
                    <li key={i}>
                      <span className="capitalize">{(ln.expense_type || "").replace(/_/g, " ")}{ln.custom_type ? ` (${ln.custom_type})` : ""}</span>
                      : ₹{ln.amount}
                      {ln.description ? ` — ${ln.description}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            );
          }
          if (k === "cost_breakdown" && typeof v === "object" && v) {
            return (
              <div key={k}>
                <div className="opacity-60 mb-1">Cost breakdown:</div>
                <ul className="pl-3 space-y-0.5">
                  {Object.entries(v).map(([kk, vv]) => (
                    <li key={kk} className="capitalize"><span>{kk.replace(/_/g, " ")}:</span> ₹{vv}</li>
                  ))}
                </ul>
              </div>
            );
          }
          return (
            <div key={k}>
              <span className="opacity-60 capitalize">{k.replace(/_/g, " ")}:</span>{" "}
              <span className="font-medium">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const DelayBadge = ({ a }) => {
    if (!a.is_delayed) return null;
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/40 px-1.5 py-0.5 rounded ml-1" title={`Overdue by ${a.overdue_hours}h`}>
        <WarningCircle size={10} weight="fill" /> Delayed
      </span>
    );
  };

  return (
    <div data-testid="approvals-page">
      <PageHeader title="Approvals" description="Travel, expense & other approval workflows." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="approvals-pending-tab">Pending ({counts.pending})</TabsTrigger>
          <TabsTrigger value="completed" data-testid="approvals-completed-tab">Completed ({counts.completed})</TabsTrigger>
          <TabsTrigger value="paid" data-testid="approvals-paid-tab">Paid ({counts.paid})</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4 space-y-3">
          {filtered.length === 0 && <div className="text-sm text-slate-500 text-center py-10">No approvals to show.</div>}
          {filtered.map((a) => (
            <Card
              key={a.id}
              className="p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => openOne(a)}
              data-testid={`approval-card-${a.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 flex-wrap">
                    <span>{a.type}</span>
                    <span>•</span>
                    <span>{a.applicant_name}</span>
                    <DelayBadge a={a} />
                  </div>
                  <div className="text-sm font-medium mt-1">{a.summary}</div>
                  <div className="text-xs text-slate-400 mt-1">
                    {a.status === "paid"
                      ? `Paid • ${fmtDateTime(a.paid_at)}`
                      : a.current_step >= (a.chain?.length || 0)
                        ? "All stages completed"
                        : `Step ${a.current_step + 1} of ${a.chain.length} • ${a.chain[a.current_step]?.replace(/_/g, " ")}`}
                    {a.auto_pay_on_approval && a.status !== "paid" && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-[#fd7e14]">
                        • Auto-pay on approval
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <StatusPill status={a.status} />
                  <div className="text-xs text-slate-400 mt-1">{fmtDateTime(a.created_at)}</div>
                  {a.can_act && <span className="inline-block mt-1 text-[10px] uppercase tracking-wider font-semibold text-[#fd7e14]">Action Required</span>}
                </div>
              </div>
            </Card>
          ))}

          <Pagination total={total} filters={filters} setFilters={setFilters} testid="approvals-pagination" />
        </TabsContent>
      </Tabs>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="capitalize">{active?.type} approval</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-3">
              <div className="text-sm">{active.summary}</div>
              {active.auto_pay_on_approval && active.status !== "paid" && (
                <div className="text-xs rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/40 p-2 text-amber-800 dark:text-amber-200">
                  <b>Auto-pay enabled:</b> Applicant marked this as already paid. It will be auto-marked Paid upon Finance Manager approval.
                </div>
              )}
              {active.is_delayed && (
                <div className="text-xs rounded border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-700/40 p-2 text-red-700 dark:text-red-300">
                  <b>Delayed:</b> Current stage is overdue by {active.overdue_hours}h (SLA {active.sla_hours || 24}h).
                </div>
              )}

              {(active.request_attachment_ids?.length > 0 || active.request_attachment_id) && (
                <div className="text-sm">
                  <div className="text-xs uppercase tracking-wider opacity-60 mb-1">Attached document{(active.request_attachment_ids?.length || 1) > 1 ? "s" : ""}</div>
                  <div className="flex flex-wrap gap-2">
                    {(active.request_attachment_ids && active.request_attachment_ids.length > 0 ? active.request_attachment_ids : [active.request_attachment_id]).filter(Boolean).map((fid) => (
                      <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#fd7e14] underline text-sm" data-testid="approval-attachment-link">
                        📎 View document
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {renderDetails(active.request_details)}

              <ApprovalTimeline approval={active} />

              {active.can_act && (
                <>
                  <Textarea data-testid="approval-comment" placeholder="Comment (required)…" value={comment} onChange={(e) => setComment(e.target.value)} />
                  <DialogFooter>
                    <Button variant="outline" className="text-red-600" onClick={() => act("reject")} data-testid="approval-reject">Reject</Button>
                    <Button onClick={() => act("approve")} className="btn-brand" data-testid="approval-approve">Approve</Button>
                  </DialogFooter>
                </>
              )}

              {!active.can_act && active.status === "approved" && isSuperAdmin && (
                <>
                  <Textarea data-testid="approval-comment" placeholder="Comment (optional for Super Admin)…" value={comment} onChange={(e) => setComment(e.target.value)} />
                  <DialogFooter className="flex-wrap gap-2">
                    {canDelete && <Button variant="outline" className="text-red-600" onClick={() => remove(active)} data-testid="approval-delete">Delete</Button>}
                    {canDelete && <Button variant="outline" className="text-red-600" onClick={rejectApproved} data-testid="approval-reject-approved">Reject (Reverse)</Button>}
                    <Button className="btn-brand" onClick={markPaid} data-testid="approval-mark-paid">Mark as Paid</Button>
                  </DialogFooter>
                </>
              )}

              {!active.can_act && active.status === "paid" && canDelete && (
                <>
                  <Textarea data-testid="approval-comment-paid" placeholder="Comment (optional for Super Admin)…" value={comment} onChange={(e) => setComment(e.target.value)} />
                  <DialogFooter>
                    <Button variant="outline" className="text-red-600" onClick={rejectApproved} data-testid="approval-reject-paid">Reject (Reverse Paid)</Button>
                  </DialogFooter>
                </>
              )}

              {!active.can_act && !["approved", "paid"].includes(active.status) && canDelete && (
                <DialogFooter>
                  <Button variant="outline" className="text-red-600" onClick={() => remove(active)} data-testid="approval-delete">Delete</Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
